import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { FaTrash, FaFileExport, FaDownload, FaFileCode, FaFileArchive, FaUndo } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AzimuthDatumBar } from './AzimuthDatumBar';
import { DepthRunPanel } from './DepthRunPanel';
import { TimeBasedChart } from './TimeBasedChart';
import { TwoSeriesTimeBasedChart } from './charts/TwoSeriesTimeBasedChart';
import { GyroRecordChart } from './charts/GyroRecordChart';
import { OrientationRecordChart } from './charts/OrientationRecordChart';
import { GammaRecordChart } from './charts/GammaRecordChart';
import { positionsByShot } from '../../surveyPositions';
import { exportSurveys } from '../../functions/surveyExport';
import { prefersDip } from '../../anglePreference';
import { formatDate, formatDateTime } from '../../i18n/format';

// A Depth table cell that admins can click to edit; non-admins see a plain
// value. Commits on Enter or blur, cancels on Escape.
const EditableDepthCell = ({ depth, shotId, isAdmin, onSave }) => {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');

    if (!isAdmin) {
        return <td>{depth.toFixed(2)}</td>;
    }

    const commit = () => {
        setEditing(false);
        const next = parseFloat(value);
        if (!isNaN(next) && next !== depth) {
            onSave(shotId, next);
        }
    };

    if (editing) {
        return (
            <td>
                <input type="number" step="0.01" autoFocus value={value}
                    className="form-control form-control-sm" style={{ width: 90 }}
                    onChange={e => setValue(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        else if (e.key === 'Escape') setEditing(false);
                    }} />
            </td>
        );
    }

    return (
        <td onClick={() => { setValue(String(depth)); setEditing(true); }}
            style={{ cursor: 'pointer' }} title={t('tableTab.clickToEditDepth')}>
            {depth.toFixed(2)}
        </td>
    );
};

export const TableTab = ({ drillhole, initialFileId, handleDrillholeUpdate, industry }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    // Identified by fileId, not fileName: a customer can upload two surveys with
    // the same name (the tool only suffixes a timestamp for some device types),
    // and selecting by name always resolved to whichever was first in the list.
    const [selectedFileId, setSelectedFileId] = useState('');
    const [survey, setSurvey] = useState({});
    const [rawData, setRawData] = useState({});
    const [gridCorrection, setGridCorrection] = useState(null);
    const [gridCorrections, setGridCorrections] = useState([]);
    const [angleDisplay, setAngleDisplay] = useState(null);
    const [showPosition, setShowPosition] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [exportError, setExportError] = useState('');
    const [returnError, setReturnError] = useState('');
    const showDip = prefersDip(angleDisplay, industry);
    // The same rule as anglePreference.angleHeader, which keeps returning the English
    // constants because they are also compared against the stored setting.
    const angleLabel = t(showDip ? 'survey.dip' : 'survey.inclination');
    // The corrected column is shown when a correction actually applies to
    // this survey - the same condition as the banner above the table - so it
    // never appears as a duplicate of the true-north column.
    const showCorrected = !!gridCorrection;

    // Grid-corrected azimuth, wrapped the same way the server does
    // (Services/SurveyExportRender.Wrap360). The old inline `% 360` left a
    // negative result negative, so a hole pointing just west of north would have
    // shown -0.57 on screen while the exported file said 359.43.
    const correctedAzimuth = (azimuth) => {
        const wrapped = (azimuth + (gridCorrection?.correction || 0)) % 360;
        return (wrapped < 0 ? wrapped + 360 : wrapped).toFixed(2);
    };

    useEffect(() => {
        fetch('api/identity/getuser')
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                if (data?.roles?.some(role => role.toLowerCase() === 'admin')) setIsAdmin(true);
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch('api/identity/customersettings');
                if (response.ok) {
                    const settings = await response.json();
                    setGridCorrections(settings.gridCorrections || []);
                    setAngleDisplay(settings.angleDisplay || null);
                }
            } catch (error) {
                console.error('Error fetching customer settings:', error);
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        if (survey && survey.Created && gridCorrections.length > 0) {
            const surveyDate = new Date(survey.Created);
            // Sort corrections by date descending to find the most recent one that applies
            const sortedCorrections = [...gridCorrections].sort((a, b) =>
                new Date(b.appliesFrom) - new Date(a.appliesFrom)
            );
            // Find the first correction where appliesFrom is <= surveyDate
            const applicable = sortedCorrections.find(c => new Date(c.appliesFrom) <= surveyDate);
            setGridCorrection(applicable || null);
        } else {
            setGridCorrection(null);
        }
    }, [survey, gridCorrections]);



    const handleSurveyDelete = () => {
        if (window.confirm(t('tableTab.confirmDeleteSurvey'))) {
            const surveyReference = drillhole.surveys.find(survey => survey.fileId === selectedFileId);
            if (!surveyReference) {
                return;
            }
            fetch('api/projects/deletesurvey', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'survey': surveyReference.key
                }
            })
                .then(response => response.json())
                .then(data => {
                    handleDrillholeUpdate(data);
                    setSelectedFileId('');
                    setSurvey({});
                }
                );
        }
    }

    // Un-approves the survey so a shot can be re-sourced, or so it can be approved
    // to a different drillhole. Only the map changes: the shots keep their Approved
    // flags, and the approval page reopens with them. Linked by fileId, not name,
    // because a name can already be in the queue.
    const handleReturnToApproval = () => {
        if (!window.confirm(t('tableTab.confirmReturnToApproval'))) {
            return;
        }
        const surveyReference = drillhole.surveys.find(survey => survey.fileId === selectedFileId);
        if (!surveyReference) {
            return;
        }
        setReturnError('');
        fetch('api/projects/returnsurveytoapproval', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: surveyReference.key })
        })
            .then(response => response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`)))
            .then(data => {
                const stillApproved = (data.projects || []).some(p => (p.drillholes || []).some(
                    d => (d.surveys || []).some(s => s.key === surveyReference.key)));
                handleDrillholeUpdate(data);
                if (stillApproved) {
                    setReturnError(t('tableTab.returnToApprovalNotMoved'));
                    return;
                }
                setSelectedFileId('');
                setSurvey({});
                navigate(`/approval?survey=${encodeURIComponent(surveyReference.fileId)}`);
            })
            .catch(err => setReturnError(t('tableTab.returnToApprovalFailed', { status: err.message })));
    }

    // The CSV is rendered server-side under the customer's export template, so
    // this button, the whole-drillhole button and the bulk review page all
    // produce the same shape of file. Columns layout with a single survey adds
    // no key columns, which is what keeps the output identical to what this
    // button produced when it built the CSV itself.
    const handleSurveyShare = async () => {
        setExportError('');
        try {
            await exportSurveys([selectedFileId], 'Columns', (survey.Name || 'survey') + '.csv');
        } catch (err) {
            setExportError(err.message);
        }
    }

    const handleSurveySelect = (event) => {
        if (event.target.value === '') {
            return;
        }
        setSelectedFileId(event.target.value);
        const surveyReference = drillhole.surveys.find(survey => survey.fileId === event.target.value);
        loadSurvey(surveyReference);
    }

    const loadSurvey = async (surveyReference) => {
        if (!surveyReference) {
            return;
        }
        setLoading(true);
        const response = await fetch('api/surveys/' + surveyReference.fileId, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        const data = await response.json();
        setSurvey(data);
        setLoading(false);
        setRawData({});
    }

    const printRawData = (data, source) => {
        data.source = source;
        setRawData(data);
    }


    useEffect(() => {
        setLoading(false);
        if (drillhole && drillhole.surveys && drillhole.surveys.length > 0) {
            // Open on the survey a link asked for (the Core Orientation table), else
            // the first survey by the same sort the select element uses.
            const sorted = [...drillhole.surveys].sort((a, b) => a.fileName.localeCompare(b.fileName));
            const firstSurvey = sorted.find(s => initialFileId && s.fileId === initialFileId) || sorted[0];
            if (firstSurvey) {
                setSelectedFileId(firstSurvey.fileId);
                // Load survey asynchronously without making the effect async
                (async () => {
                    await loadSurvey(firstSurvey);
                })();
                return;
            }
        }
        setSelectedFileId('');
        setSurvey({});
    }, [drillhole, initialFileId]);

    // Display list for the picker: sorted for stability, and labelled so that two
    // surveys sharing a name can still be told apart. The upload time is only
    // appended where a name actually repeats, so the common case stays clean.
    const surveyOptions = useMemo(() => {
        const references = [...(drillhole.surveys || [])].sort((a, b) => a.fileName.localeCompare(b.fileName));
        const nameCounts = new Map();
        references.forEach(r => nameCounts.set(r.fileName, (nameCounts.get(r.fileName) || 0) + 1));
        return references.map(reference => ({
            reference,
            label: nameCounts.get(reference.fileName) > 1 && reference.uploaded
                ? t('tableTab.uploadedLabel', { name: reference.fileName, date: formatDateTime(reference.uploaded) })
                : reference.fileName
        }));
    }, [drillhole.surveys, t]);

    const renderFailiures = (survey) => {
        if (survey.FailedShots && survey.FailedShots.length > 0) {
            return (
                <div>
                    <h3>{t('tableTab.failedShots')}</h3>
                    <table className="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th>{angleLabel}</th>
                                <th>{t('survey.azimuth')}</th>
                                <th>Toolface</th>
                                <th>GTotal</th>
                                <th>WTotal</th>
                                <th>{t('survey.reason')}</th>
                                <th>{t('survey.timestamp')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {survey.FailedShots.map((row, index) => {
                                return (
                                    <tr key={index}>
                                        <td>{(showDip ? row.OrientationSet.Inclination - 90 : row.OrientationSet.Inclination).toFixed(2)}</td>
                                        <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                        <td>{row.OrientationSet.HighSide.toFixed(2)}</td>
                                        <td>{row.OrientationSet.GTotal.toFixed(2)}</td>
                                        <td>{row.OrientationSet.WTotal.toFixed(2)}</td>
                                        <td>{row.Reason}</td>
                                        <td>{row.TimeStamp}</td>
                                        {(row.OrientationSet.SensorSets && row.OrientationSet.SensorSets.length > 0) &&
                                            <td><button className="btn btn-sm btn-primary"
                                                onClick={() => printRawData(row.OrientationSet, t('tableTab.sourceFailedShot'))}>
                                                {t('common.raw')}</button></td>
                                        }
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )
        }
        else {
            return <div></div>
        }
    }

    const renderPreRunGyrocompasses = (survey) => {
        if (survey.PreRunGyrocompasses && survey.PreRunGyrocompasses.length > 0) {
            return (
                <div>
                    <h3>{t('tableTab.preRunGyrocompasses')}</h3>
                    <table className="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('survey.inclination')}</th>
                                <th>{t('survey.azimuth')}</th>
                                <th>Toolface</th>
                                <th>GTotal</th>
                                <th>WTotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {survey.PreRunGyrocompasses.map((row, index) => {
                                return (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{row.Inclination.toFixed(2)}</td>
                                        <td>{row.Azimuth.toFixed(2)}</td>
                                        <td>{row.HighSide.toFixed(2)}</td>
                                        <td>{row.GTotal.toFixed(2)}</td>
                                        <td>{row.WTotal.toFixed(2)}</td>
                                        {(row.SensorSets && row.SensorSets.length > 0) &&
                                            <td><button className="btn btn-sm btn-primary"
                                                onClick={() => printRawData(row, t('tableTab.sourcePreRun'))}>
                                                {t('common.raw')}</button></td>
                                        }
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )
        }
        else {
            return <div></div>
        }
    }


    const renderRawData = (data) => {
        return (
            <div>
                <h3>{t('tableTab.rawDataView')}</h3>
                <p>{t('tableTab.source', { source: data.source })}
                    <br />Inc: {data.Inclination.toFixed(2)} Az: {data.Azimuth.toFixed(2)}</p>
                {data.SensorSets && data.SensorSets.length > 1 &&
                    <><p> {t('tableTab.averageChannel', { channel: 'Kx' })} {(data.SensorSets.reduce((sum, set) => sum + set.Kx, 0) / data.SensorSets.length).toFixed(2)} <br />
                        {t('tableTab.averageChannel', { channel: 'Kz' })} {(data.SensorSets.reduce((sum, set) => sum + set.Kz, 0) / data.SensorSets.length).toFixed(2)} </p><TwoSeriesTimeBasedChart title={t('tableTab.gyro')} yAxis1Name={t('tableTab.count')} yAxis2Name={t('tableTab.count')} height={300} plotOnSameAxis={true}
                            series1Name='Kx' series1Data={data.SensorSets.map((set) => [set.TimeStamp, set.Kx])}
                            series2Name='Kz' series2Data={data.SensorSets.map((set) => [set.TimeStamp, set.Kz])} /></>
                }
                <table className="table table-striped table-sm">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Kx</th>
                            <th>Ky</th>
                            <th>Kz</th>
                            <th>Gx</th>
                            <th>Gy</th>
                            <th>Gz</th>
                            <th>{t('survey.encoder')}</th>
                            <th>{t('survey.timestamp')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.SensorSets.map((row, index) => {
                            return (
                                <tr key={index}>
                                    <td>{index}</td>
                                    <td>{row.Kx}</td>
                                    <td>{row.Ky ?? ""}</td>
                                    <td>{row.Kz}</td>
                                    <td>{row.Gx}</td>
                                    <td>{row.Gy}</td>
                                    <td>{row.Gz}</td>
                                    <td>{row.Encoder?.toFixed(1)}</td>
                                    <td>{row.TimeStamp}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        )
    }


    // The position columns belong to this table rather than to the view as a
    // whole, so the toggle sits with the heading instead of in the toolbar above.
    // Shared by all four tool-class branches below.
    const savedShotsHeading = (
        <div className="d-flex align-items-center mb-2">
            <h3 className="mb-0">{t('tableTab.savedShots')}</h3>
            <div className="form-check form-switch ms-3">
                <input className="form-check-input" type="checkbox" id="showPositionToggle"
                    checked={showPosition} onChange={e => setShowPosition(e.target.checked)} />
                <label className="form-check-label" htmlFor="showPositionToggle">{t('tableTab.showPosition')}</label>
            </div>
        </div>
    );

    const renderSurveyTable = (survey) => {
        if (survey.SavedShots && survey.SavedShots.length > 0) {
            // MiNS (case 0) and Steering (case 1) render these in array order, so sort by
            // depth ascending — the Android app stores newest-first. HDD (case 2) keeps the
            // order shots were taken (see approvedItemsInOrder below). The FastTrak branch
            // and the position calc both make their own (timestamp-ordered) copies, so
            // this depth sort only affects the MiNS/Steering render order.
            const approvedItems = survey.SavedShots.filter(item => item.Approved !== false).sort((a, b) => a.Depth - b.Depth);
            const rejectedItems = survey.SavedShots.filter(item => item.Approved === false).sort((a, b) => a.Depth - b.Depth);
            // HDD shows shots in capture order (Joint number = capture sequence).
            const approvedItemsInOrder = survey.SavedShots.filter(item => item.Approved !== false);

            // When the user has asked for position columns, read the server-computed
            // positions off each shot's PositionSet, keyed by the shot itself so each
            // row can look its own position up regardless of the table's render order.
            const positionByShot = showPosition ? positionsByShot(survey) : new Map();

            const positionHeaders = showPosition ? <><th>{t('survey.northSouth')}</th><th>{t('survey.eastWest')}</th><th>TVD</th></> : null;
            const positionCells = (row) => {
                if (!showPosition) return null;
                const p = positionByShot.get(row);
                if (!p) return <><td></td><td></td><td></td></>;
                return <><td>{p.northing.toFixed(2)}</td><td>{p.easting.toFixed(2)}</td><td>{p.tvd.toFixed(2)}</td></>;
            };

            if (survey.DeviceName && survey.DeviceName.startsWith('FastTrak')) {
                // Continuous tools record a chronological round-trip; the Android
                // app stores newest-first, so display sorted by timestamp ascending
                // to match the order the tool actually moved through the hole.
                const byTime = (a, b) => new Date(a.Timestamp) - new Date(b.Timestamp);
                const approvedSorted = [...approvedItems].sort(byTime);
                const rejectedSorted = [...rejectedItems].sort(byTime);
                return <div>{savedShotsHeading}
                    <table className="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th>{t('survey.depth')}</th>
                                <th>{angleLabel}</th>
                                <th>{t('survey.azimuthTrueNorth')}</th>
                                {showCorrected && <th>{t('survey.azimuthCorrected')}</th>}
                                <th>Highside</th>
                                <th>{t('survey.steeringAngle')}</th>
                                {positionHeaders}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <tr><td colSpan={showPosition ? 9 : 6}>{t('common.loading')}</td></tr> : approvedSorted.map((row, index) => {
                                return (
                                    <tr key={index}>
                                        <EditableDepthCell depth={row.Depth} shotId={row.Id} isAdmin={isAdmin} onSave={handleDepthEdit} />
                                        <td>{(showDip ? row.OrientationSet.Inclination - 90 : row.OrientationSet.Inclination).toFixed(2)}</td>
                                        <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                        {showCorrected && <td>{correctedAzimuth(row.OrientationSet.Azimuth)}</td>}
                                        <td>{row.OrientationSet.HighSide.toFixed(2)}</td>
                                        <td>{row.OrientationSet.SteeringAngle.toFixed(2)}</td>
                                        {positionCells(row)}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {rejectedSorted.length > 0 &&
                        <div>
                            <h3>{t('tableTab.rejectedShots')}</h3>
                            <table className="table table-striped table-sm">
                                <thead>
                                    <tr>
                                        <th>{t('survey.depth')}</th>
                                        <th>{angleLabel}</th>
                                        <th>{t('survey.azimuthTrueNorth')}</th>
                                        {showCorrected && <th>{t('survey.azimuthCorrected')}</th>}
                                        <th>Highside</th>
                                        <th>{t('survey.steeringAngle')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rejectedSorted.map((row, index) => {
                                        return (
                                            <tr key={index}>
                                                <EditableDepthCell depth={row.Depth} shotId={row.Id} isAdmin={isAdmin} onSave={handleDepthEdit} />
                                                <td>{(showDip ? row.OrientationSet.Inclination - 90 : row.OrientationSet.Inclination).toFixed(2)}</td>
                                                <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                                {showCorrected && <td>{correctedAzimuth(row.OrientationSet.Azimuth)}</td>}
                                                <td>{row.OrientationSet.HighSide.toFixed(2)}</td>
                                                <td>{row.OrientationSet.SteeringAngle.toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    }
                    <OrientationRecordChart orientationRecords={survey.OrientationRecords} depthRecords={survey.DepthTimeRecords} height={400} />
                </div>
            }

            // This is the new format
            switch (survey.DeviceType) {
                case 0:
                    //this is a MiNS survey
                    return <div>{savedShotsHeading}
                        <table className="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>{t('survey.depth')}</th>
                                    <th>{angleLabel}</th>
                                    <th>{t('survey.azimuthTrueNorth')}</th>
                                    {showCorrected && <th>{t('survey.azimuthCorrected')}</th>}
                                    <th>GTotal</th>
                                    <th>WTotal</th>
                                    {positionHeaders}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? <tr><td colSpan={showPosition ? 10 : 7}>{t('common.loading')}</td></tr> : approvedItems.map((row, index) => {
                                    return (
                                        <tr key={index}>
                                            <EditableDepthCell depth={row.Depth} shotId={row.Id} isAdmin={isAdmin} onSave={handleDepthEdit} />
                                            <td>{(showDip ? row.OrientationSet.Inclination - 90 : row.OrientationSet.Inclination).toFixed(2)}</td>
                                            <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                            {showCorrected &&
                                                <td>{correctedAzimuth(row.OrientationSet.Azimuth)}</td>
                                            }
                                            <td>{row.OrientationSet.GTotal.toFixed(3)}</td>
                                            <td>{row.OrientationSet.WTotal.toFixed(2)}</td>
                                            {positionCells(row)}
                                            {(row.OrientationSet.SensorSets && row.OrientationSet.SensorSets.length > 1) &&
                                                <td><button className="btn btn-sm btn-primary"
                                                    onClick={() => printRawData(row.OrientationSet, t('tableTab.sourceSavedShot'))}>
                                                    {t('common.raw')}</button></td>
                                            }
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {rejectedItems.length > 0 &&
                            <div>
                                <h3>{t('tableTab.rejectedShots')}</h3>
                                <table className="table table-striped table-sm">
                                    <thead>
                                        <tr>
                                            <th>{t('survey.depth')}</th>
                                            <th>{angleLabel}</th>
                                            <th>{t('survey.azimuthTrueNorth')}</th>
                                            {showCorrected && <th>{t('survey.azimuthCorrected')}</th>}
                                            <th>GTotal</th>
                                            <th>WTotal</th>
                                            <th>QA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rejectedItems.map((row, index) => {
                                            return (
                                                <tr key={index}>
                                                    <EditableDepthCell depth={row.Depth} shotId={row.Id} isAdmin={isAdmin} onSave={handleDepthEdit} />
                                                    <td>{(showDip ? row.OrientationSet.Inclination - 90 : row.OrientationSet.Inclination).toFixed(2)}</td>
                                                    <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                                    {showCorrected && <td>{correctedAzimuth(row.OrientationSet.Azimuth)}</td>}
                                                    <td>{row.OrientationSet.GTotal.toFixed(3)}</td>
                                                    <td>{row.OrientationSet.WTotal.toFixed(2)}</td>
                                                    <td>{row.OrientationSet.QA ? t('common.yes') : t('common.no')}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        }
                        <GyroRecordChart gyroRecords={survey.GyroRecords} depthRecords={survey.DepthTimeRecords} height={400} />
                    </div>
                case 1:
                    //this is a Steering survey
                    return <div>{savedShotsHeading}
                        <table className="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>{t('survey.depth')}</th>
                                    <th>{t('survey.inclination')}</th>
                                    <th>{t('survey.azimuth')}</th>
                                    <th>Highside</th>
                                    <th>{t('survey.steeringAngle')}</th>
                                    {positionHeaders}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? <tr><td colSpan={showPosition ? 8 : 5}>{t('common.loading')}</td></tr> : approvedItems.map((row, index) => {
                                    return (
                                        <tr key={index}>
                                            <EditableDepthCell depth={row.Depth} shotId={row.Id} isAdmin={isAdmin} onSave={handleDepthEdit} />
                                            <td>{row.OrientationSet.Inclination.toFixed(2)}</td>
                                            <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                            <td>{row.OrientationSet.HighSide.toFixed(2)}</td>
                                            <td>{row.OrientationSet.SteeringAngle.toFixed(2)}</td>
                                            {positionCells(row)}
                                            {(row.OrientationSet.SensorSets && row.OrientationSet.SensorSets.length > 0) &&
                                                <td><button className="btn btn-sm btn-primary"
                                                    onClick={() => printRawData(row.OrientationSet, t('tableTab.sourceSavedShot'))}>
                                                    {t('common.raw')}</button></td>
                                            }
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                case 2:
                    //this is an HDD survey
                    return <div>{savedShotsHeading}
                        <table className="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>{t('survey.joint')}</th>
                                    <th>{t('survey.depth')}</th>
                                    <th>{t('survey.inclination')}</th>
                                    <th>{t('survey.azimuth')}</th>
                                    <th>{t('survey.azimuthMagnetic')}</th>
                                    <th>{t('survey.leftRight')}</th>
                                    <th>{t('survey.away')}</th>
                                    <th>{t('survey.elevation')}</th>
                                    {positionHeaders}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? <tr><td colSpan={showPosition ? 11 : 8}>{t('common.loading')}</td></tr> : approvedItemsInOrder.map((row, index) => {
                                    return (
                                        <tr key={index}>
                                            <td>{index}</td>
                                            <EditableDepthCell depth={row.Depth} shotId={row.Id} isAdmin={isAdmin} onSave={handleDepthEdit} />
                                            <td>{row.OrientationSet.Inclination.toFixed(2)}</td>
                                            <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                            <td>{row.OrientationSet.MagneticAzimuth ? row.OrientationSet.MagneticAzimuth.toFixed(2) : ''}</td>
                                            <td>{row.PositionSet.LeftRight.toFixed(2)}</td>
                                            <td>{row.PositionSet.Away.toFixed(2)}</td>
                                            <td>{row.PositionSet.Elevation.toFixed(2)}</td>
                                            {positionCells(row)}
                                            {(row.OrientationSet.SensorSets && row.OrientationSet.SensorSets.length > 0) &&
                                                <td><button className="btn btn-sm btn-primary"
                                                    onClick={() => printRawData(row.OrientationSet, t('tableTab.sourceSavedShot'))}>
                                                    {t('common.raw')}</button></td>
                                            }
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                default:
                    return <p>{t('tableTab.unknownSurveyType')}</p>
            }


        }
    }

    const renderWirelineVoltages = (survey) => {
        if (survey.WirelinePsuRecords && survey.WirelinePsuRecords.length > 0) {
            return survey.WirelinePsuRecords && (
                <div>
                    <div className="overflow-auto">
                        <h3>{t('tableTab.wirelinePsuRecords')}</h3>
                        <TimeBasedChart title={t('tableTab.wirelinePsuVoltage')} yAxisName={t('tableTab.voltage')} height={300} seriesName={t('tableTab.highCurrent')}
                            seriesData={survey.WirelinePsuRecords.map((survey) => [survey.Recieved, survey.HighVoltage])} />

                    </div>
                </div>
            )
        }
        else {
            return <div></div>
        }
    }

    const renderBatteryVoltages = (survey) => {
        if (survey.BatteryRecords && survey.BatteryRecords.length > 0) {
            return survey.BatteryRecords && (
                <div>
                    <div className="overflow-auto">
                        <h3>{t('tableTab.batteryRecords')}</h3>
                        <TimeBasedChart title={t('tableTab.batteryVoltage')} yAxisName={t('tableTab.voltage')} height={300} seriesName={t('tableTab.batteryVoltage')}
                            seriesData={survey.BatteryRecords.map((survey) => [survey.Recieved, survey.BatteryVolts])} />
                    </div>
                </div>
            )
        }
        else {
            return <div></div>
        }
    }

    const renderGammaRecords = (survey) => {
        if (survey.GammaRecords && survey.GammaRecords.length > 0) {
            return (
                <div>
                    <div className="overflow-auto">
                        <h3>{t('tableTab.gammaRecords')}</h3>
                        <GammaRecordChart gammaRecords={survey.GammaRecords}
                            depthRecords={survey.DepthTimeRecords} height={400} />
                    </div>
                </div>
            )
        }
        else {
            return <div></div>
        }
    }

    const renderPressureRecords = (survey) => {
        if (survey.PressureRecords && survey.PressureRecords.length > 0) {
            return survey.PressureRecords && (
                <div>
                    <div className="overflow-auto">
                        <h3>{t('tableTab.pressureRecords')}</h3>
                        <TwoSeriesTimeBasedChart title={t('tableTab.pressure')} yAxis1Name={t('tableTab.pressure')} yAxis2Name='' height={300} plotOnSameAxis={true}
                            series1Name={t('tableTab.annulus')} series1Data={survey.PressureRecords.map((survey) => [survey.TimeStamp, survey.Pressure1])}
                            series2Name={t('tableTab.pipe')} series2Data={survey.PressureRecords.map((survey) => [survey.TimeStamp, survey.Pressure2])} />
                    </div>
                </div>
            )
        }
        else {
            return <div></div>
        }
    }

    const renderGridCorrection = () => {
        if (!gridCorrection) {
            return null;
        }
        const formattedDate = formatDate(gridCorrection.appliesFrom);
        return (
            <div className="alert alert-info" role="alert">
                <strong>{t('tableTab.gridCorrection')}</strong> {t('tableTab.gridCorrectionValue', { correction: gridCorrection.correction, date: formattedDate })}
            </div>
        );
    };

    const renderSurveyMetadata = (survey) => {
        const hasAny = survey.DeviceName || (survey.DeviceId !== undefined && survey.DeviceId !== null) || survey.AppVersion;
        if (!hasAny) return null;
        return (
            <div className="mb-2 small text-muted">
                {survey.DeviceName && <span className="me-3"><strong>{t('tableTab.device')}</strong> {survey.DeviceName}</span>}
                {survey.DeviceId !== undefined && survey.DeviceId !== null && <span className="me-3"><strong>{t('tableTab.deviceId')}</strong> {survey.DeviceId}</span>}
                {survey.AppVersion && <span className="me-3"><strong>{t('tableTab.appVersion')}</strong> {survey.AppVersion}</span>}
            </div>
        );
    };

    const renderResults = (survey) => {
        // A gamma logger produces records but no shots, so the shot list alone is
        // no longer enough to decide there is something to show.
        return (survey.SavedShots || survey.GammaRecords) && (
            <div className="overflow-auto">
                {renderSurveyMetadata(survey)}
                {renderGridCorrection()}
                <AzimuthDatumBar survey={survey} surveyKey={selectedFileId}
                    canEdit={isAdmin} onSurveyUpdated={setSurvey} />
                {renderSurveyTable(survey)}
                {renderPreRunGyrocompasses(survey)}
                {renderFailiures(survey)}
                {rawData && rawData.SensorSets && renderRawData(rawData)}
                {renderGammaRecords(survey)}
                {renderWirelineVoltages(survey)}
                {renderBatteryVoltages(survey)}
                {renderPressureRecords(survey)}
                {/* Last, after the survey's own data: this is supporting evidence
                    about how the run was made, not part of the survey. Renders
                    nothing unless a depth counter was used, which is most surveys —
                    so it is dropped in bare, with no wrapper. */}
                <DepthRunPanel survey={survey} />
            </div>
        )
    }

    const handleSurveyDownload = () => {
        const blob = new Blob([JSON.stringify(survey)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${survey.Name}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // Admin-only: persist a corrected shot depth to S3 and refresh the survey
    // (positions are recomputed server-side and returned).
    const handleDepthEdit = async (shotId, depth) => {
        const surveyReference = drillhole.surveys.find(s => s.fileId === selectedFileId);
        if (!surveyReference) return;
        const response = await fetch(`api/surveys/${surveyReference.fileId}/depth`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shotId, depth })
        });
        if (response.ok) {
            setSurvey(await response.json());
        }
    };

    // CSV download is a zip of per-stream CSV files built server-side (saved
    // shots with positions plus whatever record streams the tool captured).
    const handleCsvDownload = async () => {
        const surveyReference = drillhole.surveys.find(s => s.fileId === selectedFileId);
        if (!surveyReference) return;
        const response = await fetch(`api/surveys/${surveyReference.fileId}/csv`);
        if (!response.ok) return;
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${survey.Name}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    return (
        <div className="container" style={{ marginTop: 10 }}>
            <div className="row">
                <div className="col-lg-12">
                    <div className="d-flex">
                        <h3>{t('tableTab.title')}</h3>
                    </div>
                </div>
                {returnError && (
                    <div className="col-lg-12">
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            {returnError}
                            <button type="button" className="btn-close" onClick={() => setReturnError('')}></button>
                        </div>
                    </div>
                )}
                {exportError && (
                    <div className="col-lg-12">
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            {exportError}
                            <button type="button" className="btn-close" onClick={() => setExportError('')}></button>
                        </div>
                    </div>
                )}
                {/* The select has the row to itself and the actions wrap beneath it,
                    so a phone never has to scroll sideways to reach Delete. */}
                <div className="col-lg-12">
                    <select className="form-select form-select-sm" value={selectedFileId} onChange={handleSurveySelect} style={{ maxWidth: 420 }}>
                        <option value=''>{t('tableTab.selectSurvey')}</option>
                        {surveyOptions.map(({ reference, label }) => (
                            <option key={reference.fileId} value={reference.fileId}>{label}</option>
                        ))}
                    </select>
                    {selectedFileId !== '' && !loading && (
                        <div className="d-flex flex-wrap gap-2 mt-2 mb-2">
                            {isAdmin && (
                                <Dropdown isOpen={downloadOpen} toggle={() => setDownloadOpen(o => !o)}>
                                    {/* color, not className: reactstrap adds its own btn-secondary
                                        otherwise, which wins and renders the toggle gold. */}
                                    <DropdownToggle caret color="primary" className="text-nowrap"><FaDownload /> {t('common.download')}</DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem onClick={handleSurveyDownload}><FaFileCode /> JSON</DropdownItem>
                                        <DropdownItem onClick={handleCsvDownload}><FaFileArchive /> CSV (.zip)</DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            )}
                            <button className="btn btn-primary text-nowrap" onClick={() => handleSurveyShare()}><FaFileExport /> {t('common.export')}</button>
                            {isAdmin && (
                                <button className="btn btn-primary text-nowrap" onClick={handleReturnToApproval}
                                    title={t('tableTab.returnToApprovalTitle')}><FaUndo /> {t('tableTab.returnToApproval')}</button>
                            )}
                            <button className="btn btn-primary text-nowrap" onClick={() => handleSurveyDelete()}><FaTrash /> {t('common.delete')}</button>
                        </div>
                    )}
                </div>
                <div className="col-lg-12">
                    <div className="overflow-auto">
                        {loading ?
                            <p><em>{t('common.loading')}</em></p> :
                            renderResults(survey)
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}
